/**
 * Hook for logging J-Code status analytics events to BigQuery.
 *
 * @module logJCodeStatusEvents
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useInitialDataOfSingleMedicalOrder } from "../context/initialDataOfSingleMedicalOrder";
import {
  GeneralOrderDetails,
  JCodeDetails,
  SingleCoverage,
} from "../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../enums/eventsData";
import { EventsName } from "../enums/eventsName";
import { RootState } from "../redux/store/store";
import { getAuthStatusOfOneJCode } from "../utils/getAuthStatusOfMedicalPaOrder";
import { getOrgIdForFetchExternalWorklist } from "../utils/organizationHelper";
import { apiWorkerManager } from "../workerManagers/workerManager";
import { Submission } from "./singleMedicalPaOrderDetails/useSubmissionInformation";

/**
 * Hook that returns a function for logging J-Code status events to BigQuery.
 *
 * Builds per-J-code event payloads containing order/user/coverage metadata,
 * ICD code change detection, and auth status, then enqueues them for
 * background processing via {@link apiWorkerManager}.
 *
 * @param generalOrderDetails - Postgres-backed general order details (demographics, order_id)
 * @param coverageDetails - Coverage entries used to resolve primary payer/plan names
 * @param submissionData - Submission record supplying current primary diagnosis ICD codes
 * @returns An async function `(jCodes, delay) => void` that logs each J-code's status
 */
export const useLogJCodeStatusEvents = (
  generalOrderDetails: GeneralOrderDetails,
  coverageDetails: SingleCoverage[],
  submissionData: Submission,
) => {
  const { data: authStatusOptions } = useSelector(
    (s: RootState) => s.authStatusOptions,
  );
  const { user } = useSelector((s: RootState) => s.firebaseAuthentication);
  const { oldPrimaryIcdCodes, countOfPrimaryIcdCodes } =
    useInitialDataOfSingleMedicalOrder();

  const newPrimaryIcdCodes = useMemo(() => {
    return (
      submissionData?.primary_diagnosis_details?.map(
        (detail) => detail.icd_code ?? "",
      ) ?? []
    );
  }, [submissionData]);

  const primaryCoverage = coverageDetails?.find(
    (coverage) => coverage.coverage_type === "primary",
  );

  const logJCodeStatusEvents = async (
    jCodes: JCodeDetails[],
    delay: number,
  ) => {
    if (!Array.isArray(jCodes) || jCodes.length === 0) return;
    if (!authStatusOptions) return;

    const patient_id =
      generalOrderDetails?.demographics?.patient_mrn ??
      generalOrderDetails?.demographics.patient_id ??
      "";
    const order_id = generalOrderDetails?.order_id ?? "";
    const org_id = getOrgIdForFetchExternalWorklist() ?? "";
    const user_id = user?.id ?? "";
    const user_email = user?.email ?? "";
    const payer_name = primaryCoverage?.payer_name ?? "";
    const plan_name = primaryCoverage?.plan_name ?? "";
    const itemsToStore = jCodes
      .map((jCode) => {
        const authStatus = getAuthStatusOfOneJCode(jCode, authStatusOptions);

        return {
          patient_id,
          order_id,
          org_id,
          event_name: EventsName.J_CODE_STATUS,
          user_id,
          user_email,
          additional_data: {
            screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
            payer_name,
            plan_name,
            j_code: jCode.j_code,
            auth_status: authStatus?.text ?? "",
            icd_state: jCode.nccn_match ?? "",
            old_primary_icd_codes: (oldPrimaryIcdCodes ?? []).join(","),
            count_of_icd_codes: countOfPrimaryIcdCodes ?? 0,
            new_primary_icd_codes: newPrimaryIcdCodes.join(","),
            icd_codes_changed: (() => {
              const oldCodes = (oldPrimaryIcdCodes ?? []).join(",");
              const newCodes = newPrimaryIcdCodes.join(",");
              // Assume no change to avoid false positives
              if (oldCodes === "" && (countOfPrimaryIcdCodes ?? 0) > 0) {
                return false;
              }

              return oldCodes !== newCodes;
            })(),
          },
        };
      })
      .filter(Boolean);

    if (itemsToStore.length === 0) return;

    const batchKey = `JCODE_LOG_QUEUE_${order_id}_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    apiWorkerManager.enqueueAndProcess(itemsToStore, batchKey, delay);
  };

  return logJCodeStatusEvents;
};
